import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { MetricCard } from '@/components/molecules/MetricCard';
import { ChangePasswordForm } from '@/components/organisms/ChangePasswordForm';
import { ChangeEmailForm } from '@/components/organisms/ChangeEmailForm';
import { Spinner } from '@/components/atoms/Spinner';
import { Building2, Users, Calendar } from 'lucide-react';
import { getCompanyInfo } from '@/services/api/company.api';
import type { CompanyInfo } from '@/services/api/company.api';
import styles from './ProfilePage.module.scss';

// TODO Phase 5+: Implement actual profile update API, avatar upload
export function ProfilePage() {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
    });
    setIsEditing(false);
  };

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        setIsLoadingCompany(true);
        const info = await getCompanyInfo();
        setCompanyInfo(info);
      } catch (error) {
        console.error('Failed to load company info:', error);
      } finally {
        setIsLoadingCompany(false);
      }
    };

    void loadCompanyInfo();
  }, []);

  return (
    <div className={styles.profile}>
      <div className={styles.profile__header}>
        <h1 className={styles.profile__title}>Profile</h1>
        <p className={styles.profile__subtitle}>Manage your account settings and company information</p>
      </div>

      <div className={styles.profile__content}>
        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.profile__avatarSection}>
              <Avatar src={undefined} alt={user?.name || 'User'} size="xl" fallback={user?.name || 'U'} />
              <div className={styles.profile__avatarInfo}>
                <p className={styles.profile__avatarName}>{user?.name}</p>
                <p className={styles.profile__avatarEmail}>{user?.email}</p>
                <p className={styles.profile__avatarRole}>Company Administrator</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className={styles.profile__form}>
              <FormField
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                required
              />

              <FormField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!isEditing}
                required
              />

              <div className={styles.profile__actions}>
                {isEditing ? (
                  <>
                    <Button type="submit" variant="primary">
                      Save Changes
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button type="button" variant="primary" onClick={() => setIsEditing(true)}>
                    Edit Profile
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Change email</CardTitle>
          </CardHeader>
          <CardContent>
            <ChangeEmailForm />
          </CardContent>
        </Card>

        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
          </CardHeader>
          <CardContent>
            <ChangePasswordForm />
          </CardContent>
        </Card>

        <div className={styles.profile__company}>
          <h2 className={styles.profile__companyTitle}>Company Information</h2>

          {isLoadingCompany ? (
            <div className={styles.profile__loading}>
              <Spinner size="md" />
              <p>Loading company information...</p>
            </div>
          ) : companyInfo ? (
            <>
              <Card className={styles.profile__companyCard}>
                <CardContent>
                  <div className={styles.profile__companyInfo}>
                    <div className={styles.profile__companyField}>
                      <span className={styles.profile__companyLabel}>Company Name</span>
                      <span className={styles.profile__companyValue}>{companyInfo.name}</span>
                    </div>
                    <div className={styles.profile__companyField}>
                      <span className={styles.profile__companyLabel}>Member Since</span>
                      <span className={styles.profile__companyValue}>
                        {new Date(companyInfo.createdAt).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className={styles.profile__statsGrid}>
                <MetricCard
                  icon={Building2}
                  label="Company"
                  value={companyInfo.name}
                  variant="primary"
                />
                <MetricCard
                  icon={Users}
                  label="Active Employees"
                  value={`${companyInfo.activeEmployees}/${companyInfo.totalEmployees}`}
                  variant="success"
                />
                <MetricCard
                  icon={Calendar}
                  label="Member Since"
                  value={new Date(companyInfo.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                  variant="primary"
                />
              </div>
            </>
          ) : (
            <Card className={styles.profile__companyCard}>
              <CardContent>
                <p>Failed to load company information.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
